import * as Sentry from '@sentry/react'

export class SignedUrlOssUploadError extends Error {
  httpStatus?: number
  responseBody?: string

  constructor(
    message: string,
    options?: { httpStatus?: number; responseBody?: string }
  ) {
    super(message)
    this.name = 'SignedUrlOssUploadError'
    this.httpStatus = options?.httpStatus
    this.responseBody = options?.responseBody
  }
}

export type SignedUrlOssUploadFeature =
  | 'product-source'
  | 'user-standard'
  | 'compliance-image'
  | 'machinery-e-plan'
  | 'packaging-item-document'
  | 'packaging-shipment-sku-file'

export type SignedUrlOssUploadContext = {
  feature: SignedUrlOssUploadFeature
  fileName: string
  fileSizeBytes: number
  contentType: string
  productId?: string
  sourceId?: string
  userStandardId?: string
  complianceId?: string
  documentId?: string
  documentType?: string
}

export type SignedUrlOssUploadDiagnostics = {
  uploadDurationMs?: number
  fileMimeType?: string
  fileExtension?: string
}

export type SignedUrlUploadFailureKind =
  | 'network'
  | 'http'
  | 'abort'
  | 'unknown'

export type SignedUploadUrlSummary = {
  host?: string
  pathname?: string
  signedUrlHasRequiredParams?: boolean
  signedUrlExpiresAt?: string
  signedUrlSecondsUntilExpiry?: number
}

const MAX_RESPONSE_BODY_LENGTH = 500
const MAX_ERROR_MESSAGE_LENGTH = 200

export function classifySignedUrlUploadFailure(
  error: unknown
): SignedUrlUploadFailureKind {
  if (error instanceof SignedUrlOssUploadError) {
    return 'http'
  }
  if (error instanceof DOMException && error.name === 'AbortError') {
    return 'abort'
  }
  if (error instanceof Error && error.name === 'AbortError') {
    return 'abort'
  }
  if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
    return 'network'
  }
  return 'unknown'
}

export function extractFileExtension(fileName: string): string | undefined {
  const dotIndex = fileName.lastIndexOf('.')
  if (dotIndex <= 0 || dotIndex === fileName.length - 1) {
    return undefined
  }
  return fileName.slice(dotIndex + 1).toLowerCase()
}

export function summarizeSignedUploadUrl(url: string): SignedUploadUrlSummary {
  try {
    const urlObject = new URL(url)
    const params = urlObject.searchParams
    const hasExpires = params.has('Expires')
    const hasSignature = params.has('Signature')
    const hasAccessKeyId = params.has('OSSAccessKeyId')
    const signedUrlHasRequiredParams =
      hasExpires && hasSignature && hasAccessKeyId

    let signedUrlExpiresAt: string | undefined
    let signedUrlSecondsUntilExpiry: number | undefined
    const expiresRaw = params.get('Expires')
    if (expiresRaw != null) {
      const expiresSeconds = Number.parseInt(expiresRaw, 10)
      if (!Number.isNaN(expiresSeconds)) {
        const expiresMs = expiresSeconds * 1000
        signedUrlExpiresAt = new Date(expiresMs).toISOString()
        signedUrlSecondsUntilExpiry = Math.round(
          (expiresMs - Date.now()) / 1000
        )
      }
    }

    return {
      host: urlObject.host,
      pathname: urlObject.pathname,
      signedUrlHasRequiredParams,
      signedUrlExpiresAt,
      signedUrlSecondsUntilExpiry,
    }
  } catch {
    return {}
  }
}

function truncateText(value: string, maxLength: number): string {
  if (value.length <= maxLength) {
    return value
  }
  return `${value.slice(0, maxLength)}…`
}

function truncateResponseBody(value: unknown): string | undefined {
  if (value == null) {
    return undefined
  }
  if (typeof value === 'string') {
    return truncateText(value, MAX_RESPONSE_BODY_LENGTH)
  }
  try {
    const serialized = JSON.stringify(value)
    return truncateText(serialized, MAX_RESPONSE_BODY_LENGTH)
  } catch {
    return undefined
  }
}

function buildErrorDiagnostics(error: unknown): {
  errorName?: string
  errorMessage?: string
  errorCause?: string
} {
  if (!(error instanceof Error)) {
    return {}
  }

  const diagnostics: {
    errorName?: string
    errorMessage?: string
    errorCause?: string
  } = {
    errorName: error.name,
    errorMessage: truncateText(error.message, MAX_ERROR_MESSAGE_LENGTH),
  }

  if ((error as any).cause instanceof Error) {
    diagnostics.errorCause = truncateText(
      (error as any).cause.message,
      MAX_ERROR_MESSAGE_LENGTH
    )
  }

  return diagnostics
}

function buildUploadFailureExtra(error: unknown): {
  httpStatus?: number
  responseBody?: string
} {
  if (error instanceof SignedUrlOssUploadError) {
    return {
      httpStatus: error.httpStatus,
      responseBody: truncateResponseBody(error.responseBody),
    }
  }

  return {}
}

function buildNetworkFailureExtra(uploadUrlHost?: string): {
  navigatorOnLine?: boolean
  pageOrigin?: string
  isCrossOriginUpload?: boolean
} {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') {
    return {}
  }

  const pageOrigin = window.location.origin
  let isCrossOriginUpload: boolean | undefined
  if (uploadUrlHost != null) {
    try {
      isCrossOriginUpload = new URL(pageOrigin).host !== uploadUrlHost
    } catch {
      isCrossOriginUpload = undefined
    }
  }

  return {
    navigatorOnLine: navigator.onLine,
    pageOrigin,
    isCrossOriginUpload,
  }
}

export function reportSignedUrlOssUploadFailure(input: {
  signedUrl: string
  context: SignedUrlOssUploadContext
  error: unknown
  diagnostics?: SignedUrlOssUploadDiagnostics
}): void {
  const uploadUrlSummary = summarizeSignedUploadUrl(input.signedUrl)
  const failureKind = classifySignedUrlUploadFailure(input.error)
  const uploadFailureExtra = buildUploadFailureExtra(input.error)
  const errorDiagnostics = buildErrorDiagnostics(input.error)
  const networkFailureExtra =
    failureKind === 'network'
      ? buildNetworkFailureExtra(uploadUrlSummary.host)
      : {}

  const extra = {
    ...input.context,
    ...input.diagnostics,
    uploadUrlHost: uploadUrlSummary.host,
    uploadUrlPathname: uploadUrlSummary.pathname,
    signedUrlHasRequiredParams: uploadUrlSummary.signedUrlHasRequiredParams,
    signedUrlExpiresAt: uploadUrlSummary.signedUrlExpiresAt,
    signedUrlSecondsUntilExpiry: uploadUrlSummary.signedUrlSecondsUntilExpiry,
    failureKind,
    ...errorDiagnostics,
    ...uploadFailureExtra,
    ...networkFailureExtra,
  }

  const message = `OSS signed URL upload failed (${input.context.feature})`
  const errorForSentry =
    input.error instanceof Error ? input.error : new Error(message)

  console.error('[SignedUrlOssUpload]', message, {
    context: input.context,
    error: input.error,
    extra,
  })

  Sentry.captureException(errorForSentry, {
    tags: {
      feature: input.context.feature,
      ossUpload: 'signed-url',
      failureKind,
    },
    extra,
  })
}
